import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import logoSrc from '../../assets/GymnazeLogoLightMode.png';
import { PROFILE_DESCRIPTIONS } from '../profileDescriptions';

const ARCHETYPE_COLORS = {
  "The Quiet Craftsman":      "#7B8FA1",
  "The Assertive Competitor": "#E85D04",
  "The Disciplined Driver":   "#2D9D5A",
  "The Resilient Maverick":   "#9B5DE5",
  "The Adaptive Learner":     "#0096C7",
  "The Steady Supporter":     "#C9A227",
  "The Coach's Mirror":       "#F4A261",
};

const DEFAULT_COLOR = "#9ca3af";

const s = StyleSheet.create({
  page:           { backgroundColor: '#ffffff', padding: 40, fontSize: 10, color: '#1a202c', fontFamily: 'Helvetica' },
  header:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  logo:           { width: 120 },
  headerRight:    { alignItems: 'flex-end' },
  headerTitle:    { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#1a202c' },
  headerMeta:     { fontSize: 8, color: '#6b7280', marginTop: 2 },
  divider:        { borderBottomWidth: 1, borderBottomColor: '#e5e7eb', marginVertical: 10 },
  thickDivider:   { borderBottomWidth: 2, borderBottomColor: '#1a202c', marginVertical: 12 },
  athleteBlock:   { marginBottom: 4 },
  nameRow:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  athleteName:    { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#1a202c' },
  sportBadge:     { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#6b7280', backgroundColor: '#f3f4f6', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, marginLeft: 5, textTransform: 'capitalize' },
  archetypeRow:   { flexDirection: 'row', alignItems: 'center', marginBottom: 3 },
  archetypeLabel: { fontSize: 8, color: '#6b7280', marginRight: 4 },
  archetypeName:  { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  confidenceText: { fontSize: 8, color: '#9ca3af', marginLeft: 4 },
  traitsText:     { fontSize: 8, color: '#6b7280', marginBottom: 6 },
  traitsLabel:    { fontFamily: 'Helvetica-Bold', color: '#374151' },
  levelRow:       { flexDirection: 'row' },
  levelCell:      { flex: 1, alignItems: 'center', backgroundColor: '#f9fafb', paddingVertical: 5, borderRadius: 5, marginRight: 5 },
  levelLabel:     { fontSize: 7, color: '#6b7280', marginBottom: 1 },
  levelScore:     { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#1a202c' },
  noScore:        { fontSize: 9, color: '#d1d5db' },
  noAssessment:   { fontSize: 8, color: '#9ca3af', fontStyle: 'italic' },
  pageNumber:     { position: 'absolute', bottom: 20, right: 40, fontSize: 8, color: '#9ca3af' },
});

const CoachRosterPDF = ({ athletesWithAssessments, coachName, sport, generatedDate }) => {
  return (
    <Document>
      <Page size="A4" style={s.page} wrap>

        {/* Header — fixed at top of every page */}
        <View style={s.header} fixed>
          <Image src={logoSrc} style={s.logo} />
          <View style={s.headerRight}>
            <Text style={s.headerTitle}>Athlete Roster Report</Text>
            <Text style={s.headerMeta}>
              Coach: {coachName}  ·  Sport: {sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'All'}  ·  {generatedDate}
            </Text>
          </View>
        </View>

        <View style={s.thickDivider} fixed />

        {/* Athletes */}
        {athletesWithAssessments.map((item, idx) => {
          const { athlete, latestTraitAttempt, highestScoreByLevel } = item;
          const name = `${athlete.firstName} ${athlete.lastName}`;
          const desc = latestTraitAttempt?.profile
            ? PROFILE_DESCRIPTIONS[latestTraitAttempt.profile]
            : null;
          const accentColor = latestTraitAttempt?.profile
            ? (ARCHETYPE_COLORS[latestTraitAttempt.profile] || DEFAULT_COLOR)
            : DEFAULT_COLOR;
          const levelMap = {};
          for (const ls of highestScoreByLevel) {
            levelMap[ls.level] = ls.scorePercent;
          }

          return (
            <View key={athlete._id} style={s.athleteBlock} wrap={false}>

              {/* Name + sports */}
              <View style={s.nameRow}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={s.athleteName}>{name}</Text>
                  {athlete.sports?.map(sp => (
                    <Text key={sp} style={s.sportBadge}>{sp}</Text>
                  ))}
                </View>
                {latestTraitAttempt?.dateTaken && (
                  <Text style={{ fontSize: 7.5, color: '#9ca3af' }}>
                    Assessed {new Date(Number(latestTraitAttempt.dateTaken)).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </Text>
                )}
              </View>

              {/* Archetype + traits */}
              {latestTraitAttempt?.profile ? (
                <>
                  <View style={s.archetypeRow}>
                    <Text style={s.archetypeLabel}>Archetype:</Text>
                    <Text style={[s.archetypeName, { color: accentColor }]}>
                      {latestTraitAttempt.profile}
                    </Text>
                    <Text style={s.confidenceText}>
                      ({(latestTraitAttempt.confidence * 100).toFixed(0)}% match)
                    </Text>
                  </View>
                  {desc?.dominantTraits?.length > 0 && (
                    <Text style={s.traitsText}>
                      <Text style={s.traitsLabel}>Key Traits: </Text>
                      {desc.dominantTraits.join('  ·  ')}
                    </Text>
                  )}
                </>
              ) : (
                <Text style={[s.noAssessment, { marginBottom: 6 }]}>
                  No trait assessment on record
                </Text>
              )}

              {/* Game IQ Level Scores */}
              <View style={s.levelRow}>
                {[1, 2, 3, 4, 5].map(lvl => (
                  <View key={lvl} style={s.levelCell}>
                    <Text style={s.levelLabel}>L{lvl}</Text>
                    {levelMap[lvl] != null
                      ? <Text style={s.levelScore}>{levelMap[lvl].toFixed(1)}%</Text>
                      : <Text style={s.noScore}>—</Text>
                    }
                  </View>
                ))}
              </View>

              {idx < athletesWithAssessments.length - 1 && (
                <View style={s.divider} />
              )}
            </View>
          );
        })}

        <Text
          style={s.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default CoachRosterPDF;
